<template>
  <div class="root tw:relative tw:flex-1 tw:max-w-[31rem]" :class="className">
    <div
      :class="{ 'tw:block': searchCourse.length >= 3 }"
      class="tw:hidden tw:fixed tw:inset-0 tw:z-[1000] tw:bg-transparent"
      @click="searchCourse = ''"
    />
    <div class="search tw:relative tw:flex tw:w-full tw:h-10 tw:px-2.5 tw:items-center tw:gap-4 tw:shrink-0 tw:rounded tw:z-[1002] tw:border tw:border-solid tw:border-[var(--search-background)] tw:bg-[var(--search-background)] tw:transition-[border] tw:duration-300 hover:tw:border-[var(--neutral-gray-500)]">
      <IconGoogle size="1rem" name="search" class="tw:shrink-0 tw:cursor-pointer tw:z-[1001]" @click="fetch" />
      <input
        ref="input"
        v-model="searchCourse"
        autocomplete="one-time-code"
        class="search-input tw:border-none tw:flex-1 tw:text-[var(--search-color)] tw:z-[1001] tw:outline-none tw:bg-transparent"
        :placeholder="$t('search_modal.label')"
        @keyup="handleInput"
        @keydown.enter="fetch"
      />
      <a v-if="searchCourse.length > 1 || isMobile" class="close tw:cursor-pointer" @click="clear">
        <IconGoogle class="tw:rotate-45" name="add" color="var(--subtitle-color)" />
      </a>
    </div>
    <div
      v-if="searchCourse.length >= 3 || isMobile"
      :class="{ 'search-items--mobile': isMobile }"
      class="search-items tw:absolute tw:top-[2.8rem] tw:z-[1001] tw:flex tw:w-[31rem] tw:h-auto tw:py-4 tw:flex-col tw:items-start tw:shrink-0 tw:rounded tw:border tw:border-solid tw:border-[var(--bordercolor,#404043)] tw:bg-[var(--search-background-list,#fff)] tw:shadow-sm tw:max-h-[400px] tw:overflow-auto tw:scrollbar-none"
    >
      <div v-if="loading && searchCourse.length >= 1" class="tw:items-center tw:justify-center tw:w-full tw:flex">
        <Loading />
      </div>
      <div v-else-if="isEmpty && searchCourse.length >= 1" class="tw:items-center tw:justify-center tw:w-full tw:flex tw:flex-col tw:gap-3 tw:p-8">
        <IconGoogle name="search" size="2.5rem" color="var(--maincolor)" />
        <TextPlay color="var(--title-color)" class="tw:text-center" weight="bold">
          {{ $t('search.no_results') }} <br />
          <TextPlay :text="searchCourse" color="var(--maincolor)" weight="bold" />
        </TextPlay>
        <TextPlay color="var(--title-color)" class="tw:text-center">
          Para facilitar a sua pesquisa, procure sempre digitar corretamente o termo da busca ou utilizar
          palavras-chaves.
        </TextPlay>
      </div>
      <TabsPlayV2 v-else-if="searchCourse.length >= 1" v-model="selectedTab" :tabs="tabs" class="tw:w-full" :text-size="isMobile ? 'xs' : 'base'">
        <template #course>
          <SearchList :items="courses" class="search-list" @item-click="searchCourse = ''" />
        </template>
        <template #module>
          <SearchList :items="modules" class="search-list" @item-click="searchCourse = ''" />
        </template>
        <template #lesson>
          <SearchList :items="lessons" class="search-list" @item-click="searchCourse = ''" />
        </template>
      </TabsPlayV2>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { ref, computed, onMounted, useTemplateRef } from 'vue'
import { useI18n } from 'vue-i18n'
import SearchList from '@/components/common/SearchList/SearchList.vue'
import SearchService from '@/services/resources/SearchService'
import debounce from 'lodash/debounce'

const serviceSearch = SearchService.build()
const { t } = useI18n()

const props = defineProps<{
  isMobile?: boolean
  permission?: string
  className?: string
}>()

const emit = defineEmits<{
  clear: []
}>()

const input = useTemplateRef<HTMLInputElement>('input')

const searchCourse = ref('')
const getAllSearch = ref<Record<string, unknown>>({})
const loading = ref(true)
const selectedTab = ref('module')

const courses = computed(() => (getAllSearch.value['App\\Models\\Course'] as unknown[]) || [])
const modules = computed(() =>
  ((getAllSearch.value['App\\Models\\Module'] as unknown[]) || []).filter((m: Record<string, unknown>) => m.course !== null)
)
const lessons = computed(() => (getAllSearch.value['App\\Models\\Lesson'] as unknown[]) || [])

const isEmpty = computed(() => courses.value.length === 0 && modules.value.length === 0 && lessons.value.length === 0)

const tabs = computed(() => [
  { id: 'course', icon: 'menu_book', label: `${t('search_modal.courses')} (${courses.value.length})` },
  { id: 'module', icon: 'folder', label: `${t('search_modal.modules')} (${modules.value.length})` },
  { id: 'lesson', icon: 'play_circle', label: `${t('search_modal.lessons')} (${lessons.value.length})` },
])

function handleInput() {
  loading.value = true
  lazyFetch()
}

function clear() {
  if (!searchCourse.value) {
    emit('clear')
  }
  searchCourse.value = ''
  input.value?.focus()
}

const lazyFetch = debounce(() => {
  fetch()
}, 500)

async function fetch() {
  loading.value = true
  try {
    if (searchCourse.value.length < 3) {
      getAllSearch.value = {}
      return
    }
    getAllSearch.value = await serviceSearch.search2('search_string=' + searchCourse.value)
  } finally {
    loading.value = false
  }

  // Auto-select the first tab with results
  if (courses.value.length > 0) {
    selectedTab.value = 'course'
  } else if (modules.value.length > 0) {
    selectedTab.value = 'module'
  } else if (lessons.value.length > 0) {
    selectedTab.value = 'lesson'
  }
}

onMounted(() => {
  if (props.isMobile) {
    input.value?.focus()
  }
})
</script>

<style scoped lang="scss">
.search-items--mobile {
  border: none;
  background: var(--backgroundcolor);
  left: 0;
  right: 0;
  top: 3.5rem;
  height: calc(100vh - 3.5rem);
  max-height: calc(100vh - 3.5rem);
  box-shadow: none;
  width: 100vw;
}

:deep(.tabs) {
  width: 100%;

  & .tabs-play__tabs {
    padding: 0 1rem;
  }
}
</style>
